import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Select, { components, type MultiValueProps, type StylesConfig } from "react-select";
import { useTheme } from "../../context/ThemeContext";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import useIsMonitor from "../../hooks/useIsMonitor";
import { getCustomSelectStyles } from "../../Layouts/admin/shared";
import { useMenuFavorites } from "../../components/base/AppSidebarMenu/useMenuFavorites";
import ReportLoadingPanel from "../reports/components/ReportLoadingPanel";
import { fetchQuickDaily, fetchQuickUnits } from "./quickReportsService";
import { dateInTz, favoritesFirst, filterByUnits, fmtDay, periodRange, splitToday, unitsOf, userTz } from "./quickReports.compute";
import {
  AlertsView,
  BitacoraView,
  EmptyState,
  FleetView,
  GeofencesView,
  MileageView,
  OverspeedView,
  Segmented,
  StopsView,
  type ViewProps,
} from "./QuickReportViews";
import type { QuickPeriod } from "./types";

type QuickReport = { id: string; name: string; desc: string; icon: string; View: ComponentType<ViewProps> };

// Nombres cortos a propósito: en el menú de Informes y análisis todos empezaban
// con "Informe de" y había que leer cada renglón hasta el final.
const SECTIONS: { id: string; title: string; reports: QuickReport[] }[] = [
  {
    id: "routes",
    title: "Rutas y kilometraje",
    reports: [
      { id: "fleet", name: "Resumen de flota", desc: "Km, manejo, ralentí y excesos por unidad", icon: "ri-dashboard-3-line", View: FleetView },
      { id: "mileage", name: "Kilometraje", desc: "Km por día, o por hora en Hoy y Ayer", icon: "ri-road-map-line", View: MileageView },
    ],
  },
  {
    id: "stops",
    title: "Paradas y zonas",
    reports: [
      { id: "stops", name: "Paradas", desc: "Dónde y cuánto se detuvo cada unidad", icon: "ri-stop-circle-line", View: StopsView },
      { id: "geofences", name: "Geocercas visitadas", desc: "Entradas, salidas y tiempo dentro", icon: "ri-map-pin-range-line", View: GeofencesView },
    ],
  },
  {
    id: "driving",
    title: "Conducción",
    reports: [
      { id: "bitacora", name: "Bitácora de jornada", desc: "Tramos del día de una unidad, como en el mapa", icon: "ri-steering-2-line", View: BitacoraView },
      { id: "overspeed", name: "Excesos de velocidad", desc: "Cuántos, qué tan rápido y cuánto tiempo", icon: "ri-speed-up-line", View: OverspeedView },
    ],
  },
  {
    id: "alerts",
    title: "Alertas del equipo",
    reports: [{ id: "alerts", name: "Alertas", desc: "SOS, energía desconectada y pérdida de señal", icon: "ri-alarm-warning-line", View: AlertsView }],
  },
];
const ALL_REPORTS = SECTIONS.flatMap((s) => s.reports);

const PERIODS: { value: QuickPeriod; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "yesterday", label: "Ayer" },
  { value: "last3", label: "Últimos 3 días" },
  { value: "last15", label: "Últimos 15 días" },
];
const isPeriod = (v: string | null): v is QuickPeriod => PERIODS.some((p) => p.value === v);

// Selector de unidades de alto fijo: con 10 unidades elegidas los chips
// crecían a varias líneas y empujaban la barra de filtros. Se ven las primeras
// MAX_CHIPS y un chip "+N" con el resto; la lista completa está en el desplegable.
// Una sola: en los 18rem del selector, con dos el nombre quedaba en "Cami…".
const MAX_CHIPS = 1;
type UnitOption = { value: string; label: string };
function UnitChip(props: MultiValueProps<UnitOption, true>) {
  const picked = props.selectProps.value as readonly UnitOption[];
  if (props.index < MAX_CHIPS) return <components.MultiValue {...props} />;
  if (props.index > MAX_CHIPS) return null;
  const rest = picked.slice(MAX_CHIPS);
  return (
    <span
      title={rest.map((o) => o.label).join("\n")}
      aria-label={`y ${rest.length} unidades más`}
      className="flex-shrink-0 m-0.5 px-1.5 py-0.5 rounded text-xs font-semibold tabular-nums bg-primary-500/15 text-primary-500"
    >
      +{rest.length}
    </span>
  );
}
const unitSelectStyles = (isDarkMode: boolean): StylesConfig<UnitOption, true> => {
  const base = getCustomSelectStyles(isDarkMode) as StylesConfig<UnitOption, true>;
  return {
    ...base,
    valueContainer: (p, st) => ({ ...base.valueContainer!(p, st), flexWrap: "nowrap", overflow: "hidden", maxHeight: "none" }),
    multiValue: (p, st) => ({ ...base.multiValue!(p, st), minWidth: 0, flexShrink: 1 }),
  };
};

export default function QuickReportsPage() {
  const { isDarkMode } = useTheme();
  const [params, setParams] = useSearchParams();
  const companyId = useCompaniesStore((s) => s.companyId);
  const { isMonitor, devices: monitorDevices } = useIsMonitor();
  const { favorites, toggle } = useMenuFavorites("informes-rapidos");
  const [popped, setPopped] = useState<{ id: string; n: number } | null>(null);
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const reportRef = useRef<HTMLElement>(null);

  // Informe y periodo en la URL: se pueden compartir y "Atrás" regresa al catálogo en móvil.
  const reportParam = params.get("r");
  const report = ALL_REPORTS.find((r) => r.id === reportParam) ?? ALL_REPORTS[0];
  const period: QuickPeriod = isPeriod(params.get("p")) ? (params.get("p") as QuickPeriod) : "today";
  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: key === "p" },
    );

  const tz = useMemo(userTz, []);
  const range = useMemo(() => periodRange(period, tz), [period, tz]);
  // La bitácora pide sus propios datos (una unidad, un día): no dispara ni
  // espera el resumen de TODA la flota del periodo, que en una compañía grande
  // sin precalcular tarda más de un minuto y, con el API en HTTP/1.1 (6
  // conexiones por host), dejaba en cola a la de la bitácora. Su selector sale
  // de /deviceList.
  const isBitacora = report.id === "bitacora";
  // 3 y 15 días: los días cerrados (precalculados) y «Hoy» (al vuelo, ~3 s) van
  // en dos peticiones para no retener lo que ya está listo (splitToday). La de
  // hoy comparte caché con el periodo «Hoy».
  const split = useMemo(() => splitToday(range, dateInTz(Date.now(), tz)), [range, tz]);
  const mainTo = split ? split.closed.to : range.to;
  const query = useQuery({
    queryKey: ["quick-reports", "daily", companyId, range.from, mainTo, tz],
    queryFn: ({ signal }) => fetchQuickDaily({ companyId: companyId!, from: range.from, to: mainTo, tz }, signal),
    enabled: !!companyId && !isBitacora,
    staleTime: 5 * 60_000,
  });
  const todayQuery = useQuery({
    queryKey: ["quick-reports", "daily", companyId, split?.today.from, split?.today.to, tz],
    queryFn: ({ signal }) => fetchQuickDaily({ companyId: companyId!, from: split!.today.from, to: split!.today.to, tz }, signal),
    enabled: !!companyId && !isBitacora && !!split,
    staleTime: 5 * 60_000,
  });
  const daily = useMemo(
    () =>
      split && query.data
        ? { ...query.data, items: [...query.data.items, ...(todayQuery.data?.items ?? [])], mock: query.data.mock || todayQuery.data?.mock }
        : query.data,
    [split, query.data, todayQuery.data],
  );
  const unitsQuery = useQuery({
    queryKey: ["quick-reports", "units", companyId],
    queryFn: ({ signal }) => fetchQuickUnits(companyId!, signal),
    enabled: !!companyId && isBitacora,
    staleTime: 5 * 60_000,
  });
  const active = isBitacora ? unitsQuery : query;
  // Si se llega a la bitácora con el resumen de la flota todavía en vuelo, se
  // cancela (aborta la conexión): `enabled: false` no detiene lo que ya salió.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isBitacora) queryClient.cancelQueries({ queryKey: ["quick-reports", "daily"] });
  }, [isBitacora, queryClient]);

  // Un monitor sólo ve sus equipos asignados, igual que en el selector de los informes.
  const mine = useMemo(
    () => (id: string) => !(isMonitor && Array.isArray(monitorDevices) && monitorDevices.length) || monitorDevices.includes(id),
    [isMonitor, monitorDevices],
  );
  const scoped = useMemo(() => (daily?.items ?? []).filter((it) => mine(it.device_id)), [daily, mine]);
  const units = useMemo(
    () => (isBitacora ? (unitsQuery.data ?? []).filter((u) => mine(u.id)) : unitsOf(scoped)),
    [isBitacora, unitsQuery.data, scoped, mine],
  );
  // Al cambiar de compañía la selección anterior no aplica: se ignora sin efecto aparte.
  const pickedUnits = useMemo(() => unitIds.filter((id) => units.some((u) => u.id === id)), [unitIds, units]);
  const items = useMemo(() => filterByUnits(scoped, pickedUnits), [scoped, pickedUnits]);

  const periodLabel =
    range.days.length === 1
      ? `${PERIODS.find((p) => p.value === period)!.label} · ${fmtDay(range.from)}`
      : `${fmtDay(range.from)} – ${fmtDay(range.to)}`;

  // El panel del informe tiene su propio scroll: otro informe u otro periodo
  // empiezan arriba, no donde se quedó el anterior.
  useEffect(() => {
    reportRef.current?.scrollTo({ top: 0 });
  }, [report.id, period]);

  const sections = useMemo(() => SECTIONS.map((s) => ({ ...s, reports: favoritesFirst(s.reports, favorites) })), [favorites]);

  const onStar = (id: string) => {
    const adding = !favorites.includes(id);
    toggle(id);
    setPopped(adding ? { id, n: (popped?.n ?? 0) + 1 } : null);
  };

  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const surface = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const showingReport = !!reportParam;

  const body = () => {
    if (!companyId)
      return <EmptyState isDarkMode={isDarkMode} icon="ri-building-2-line" title="Selecciona una compañía" text="Los informes rápidos se calculan por compañía." />;
    if (active.isLoading) return <ReportLoadingPanel isDarkMode={isDarkMode} icon={report.icon} title="Preparando informe" subtitle={periodLabel} />;
    if (active.isError)
      return (
        <div className="flex flex-col items-center">
          <EmptyState isDarkMode={isDarkMode} icon="ri-error-warning-line" title="No se pudo cargar el informe" text="Revisa tu conexión e inténtalo de nuevo." />
          <button
            type="button"
            onClick={() => active.refetch()}
            className="-mt-8 px-4 py-2 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white transition-[background-color,transform] duration-150 ease-out active:scale-[0.97]"
          >
            Reintentar
          </button>
        </div>
      );
    // Sin nada en los cerrados pero con hoy en camino: todavía no es «sin datos».
    if (!isBitacora && !items.length && split && todayQuery.isLoading)
      return <ReportLoadingPanel isDarkMode={isDarkMode} icon={report.icon} title="Preparando informe" subtitle={periodLabel} />;
    if (isBitacora ? !units.length : !items.length)
      return <EmptyState isDarkMode={isDarkMode} icon="ri-inbox-line" title="Sin datos en el periodo" text="No hay recorridos registrados para estas unidades. Prueba con otro periodo." />;
    const View = report.View;
    // Los cerrados ya están; hoy todavía no (o falló): se avisa sin bloquear.
    const todayNote =
      split && !isBitacora && (todayQuery.isLoading || todayQuery.isError) ? (
        <div
          role="status"
          className={`mb-4 flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${isDarkMode ? "bg-white/5 text-gray-300" : "bg-gray-50 text-gray-600"}`}
        >
          {todayQuery.isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin motion-reduce:animate-none" aria-hidden />
              Calculando hoy… los días anteriores ya están.
            </>
          ) : (
            <>
              <i className="ri-error-warning-line text-amber-500" aria-hidden />
              No se pudo cargar hoy.
              <button type="button" onClick={() => todayQuery.refetch()} className="font-medium text-primary-500 hover:underline">
                Reintentar
              </button>
            </>
          )}
        </div>
      ) : null;
    const viewUnits = pickedUnits.length ? units.filter((u) => pickedUnits.includes(u.id)) : units;
    return (
      <>
        {todayNote}
        <View items={items} units={viewUnits} days={range.days} tz={tz} isDarkMode={isDarkMode} periodLabel={periodLabel} title={report.name} />
      </>
    );
  };

  return (
    // Escritorio: catálogo e informe con scroll propio, cada uno del alto de la
    // ventana, para que uno no empuje al otro. Móvil: la página hace scroll y
    // se alternan catálogo e informe como antes.
    <div className="w-full h-full overflow-y-auto lg:overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-5 flex flex-col gap-5 lg:h-full">
        <header className="flex flex-col xl:flex-row xl:items-end gap-4 justify-between flex-shrink-0">
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${muted}`}>Informes</p>
            <h1 className={`text-2xl font-semibold tracking-tight ${strong}`}>Informes rápidos</h1>
            <p className={`text-sm ${muted}`}>Listos al instante, con los datos de cada día. Hasta 15 días.</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
            <Segmented label="Periodo" value={period} options={PERIODS} onChange={(v) => setParam("p", v)} isDarkMode={isDarkMode} />
            <div className="sm:w-72 min-w-0">
              <Select<UnitOption, true>
                inputId="qr-units"
                aria-label="Unidades"
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isDisabled={!units.length}
                options={units.map((u) => ({ value: u.id, label: u.plate ? `${u.name} · ${u.plate}` : u.name }))}
                value={units.filter((u) => pickedUnits.includes(u.id)).map((u) => ({ value: u.id, label: u.name }))}
                onChange={(sel) => setUnitIds((sel ?? []).map((o) => o.value))}
                placeholder={units.length ? `Todas las unidades (${units.length})` : "Unidades"}
                noOptionsMessage={() => "Sin unidades"}
                components={{ MultiValue: UnitChip }}
                styles={unitSelectStyles(isDarkMode)}
              />
            </div>
          </div>
        </header>

        {/* La bitácora no usa el resumen de la flota: avisa ella misma con su respuesta. */}
        {!isBitacora && daily?.mock && (
          <div role="status" className="flex-shrink-0 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-600">
            <i className="ri-flask-line mt-0.5" aria-hidden />
            <span>
              <strong className="font-semibold">Datos de ejemplo.</strong> El servicio de informes rápidos todavía no está disponible; lo que ves no
              es de tu flota.
            </span>
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-[17rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:flex-1 lg:min-h-0">
          <nav aria-label="Informes rápidos" className={`${showingReport ? "hidden lg:block" : "block"} lg:h-full lg:overflow-y-auto lg:overscroll-contain lg:pr-1`}>
            {sections.map((s) => (
              <section key={s.id} className="mb-4">
                <h2 className={`px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{s.title}</h2>
                <ul className="space-y-0.5">
                  {s.reports.map((r) => {
                    const active = r.id === report.id;
                    const fav = favorites.includes(r.id);
                    const pop = popped?.id === r.id ? popped.n : 0;
                    return (
                      <li key={r.id} className="relative group">
                        <button
                          type="button"
                          aria-current={active ? "page" : undefined}
                          onClick={() => setParam("r", r.id)}
                          className={`relative w-full flex items-start gap-3 pl-3 pr-10 py-2 rounded-xl text-left transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                            active ? "bg-primary-500/15" : `${muted} ${isDarkMode ? "hover:bg-dark-surface hover:text-white" : "hover:bg-gray-100 hover:text-gray-900"}`
                          }`}
                        >
                          {/* Tinte + barra del color del tema: legible en todos los temas, también en grayscale. */}
                          {active && <span aria-hidden className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r bg-primary-500" />}
                          <i className={`${r.icon} text-lg leading-6 flex-shrink-0 ${active ? "text-primary-500" : ""}`} aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className={`block text-sm font-medium leading-6 truncate ${strong}`}>{r.name}</span>
                            <span className={`block text-xs leading-4 mt-0.5 ${muted}`}>{r.desc}</span>
                          </span>
                        </button>
                        {/* Hermano y no anidado: un <button> dentro de otro es HTML inválido. */}
                        <button
                          type="button"
                          aria-pressed={fav}
                          aria-label={`${fav ? "Quitar de favoritos" : "Fijar en favoritos"}: ${r.name}`}
                          title={fav ? "Quitar de favoritos" : "Fijar arriba en su sección"}
                          onClick={() => onStar(r.id)}
                          className={`absolute right-1.5 top-2 w-8 h-8 flex items-center justify-center rounded-md transition-opacity duration-150 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                            fav
                              ? "opacity-100 text-amber-400"
                              : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 text-gray-400 hover:text-amber-400"
                          }`}
                        >
                          {pop > 0 && fav && (
                            <span key={`burst-${pop}`} aria-hidden className="absolute inset-1 rounded-full bg-amber-400/60 motion-safe:animate-starBurst opacity-0" />
                          )}
                          <i key={`star-${pop}`} className={`${fav ? "ri-star-fill" : "ri-star-line"} relative text-base leading-none ${pop > 0 ? "motion-safe:animate-starPop" : ""}`} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </nav>

          <section
            ref={reportRef}
            aria-label={report.name}
            className={`${showingReport ? "flex" : "hidden lg:flex"} flex-col min-w-0 rounded-2xl border p-4 sm:p-5 lg:h-full lg:overflow-y-auto lg:overscroll-contain ${surface}`}
          >
            <div className="flex items-start gap-3 mb-4 flex-shrink-0">
              <button
                type="button"
                onClick={() => setParam("r", null)}
                aria-label="Volver a informes rápidos"
                className={`lg:hidden -ml-1 w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg ${isDarkMode ? "hover:bg-white/10 text-gray-300" : "hover:bg-black/5 text-gray-600"}`}
              >
                <i className="ri-arrow-left-line text-lg" aria-hidden />
              </button>
              <div className="min-w-0">
                <h2 className={`text-lg font-semibold leading-tight ${strong}`}>{report.name}</h2>
                <p className={`text-xs mt-0.5 ${muted}`}>
                  {periodLabel}
                  {pickedUnits.length ? ` · ${pickedUnits.length} de ${units.length} unidades` : units.length ? ` · ${units.length} unidades` : ""}
                  {query.data?.items.some((i) => i.partial) ? " · hoy hasta ahora" : ""}
                </p>
              </div>
            </div>
            {/* key: cada informe y periodo entra con un fundido corto, no aparece de golpe. */}
            <div key={`${report.id}-${period}`} className="motion-safe:animate-fadeIn flex flex-col flex-1">
              {body()}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
